import { useCallback, useEffect, useState } from "react";

import { fetchCallRecordings } from "../../../data/queries/artifacts";
import { CallRecording } from "../../../types/artifact";

/**
 * Custom hook for fetching and managing call recordings for a specific interaction
 * @param interactionId The ID of the interaction to fetch call recordings for
 * @returns An object containing call recordings data, loading state, error state, and a refresh function
 */
export const useCallRecordings = (interactionId: string | null) => {
  const [recordings, setRecordings] = useState<CallRecording[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRecordings, setHasRecordings] = useState(false);

  const fetchRecordings = useCallback(async () => {
    if (!interactionId) {
      setError("No interaction ID provided");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchCallRecordings(interactionId);

      if (result.error) {
        setError(result.error.message);
        return;
      }

      if (!result.data || !result.data.recordings) {
        setRecordings([]);
        setHasRecordings(false);
        return;
      }

      setRecordings(result.data.recordings);
      setHasRecordings(result.data.recordings.length > 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [interactionId]);

  // Fetch recordings when the interaction ID changes
  useEffect(() => {
    if (interactionId) {
      fetchRecordings();
    }
  }, [interactionId, fetchRecordings]);

  return {
    recordings,
    isLoading,
    error,
    hasRecordings,
    refreshRecordings: fetchRecordings
  };
};

export default useCallRecordings;
